import { CoreAnalysisEngine, AnalysisResult } from "./coreAnalysisEngine";
import { ReactElement } from "react";
import { TFunction } from "i18next";
import { ResultDashboard } from "../components/molecules/ResultDashboard";
import { ResultCo1Demo } from "../components/molecules/ResultCo1Demo";
import {
  AnalysisEngineType,
  EngineType,
  MeanResult,
  ResultDashboardData,
  ResultMainParam,
  ResultSubParam,
} from "../types";
import { appAxios } from "../utils/appAxios";

type Co1LongVowelDetailParams = {
  ParamA?: {
    LVP_S?: number;
    LVP_J?: number;
    LVP_H?: number;
  };
  ParamB?: {
    LVP_S?: number;
    LVP_J?: number;
    LVP_H?: number;
  };
  ParamC?: {
    LVP_S?: number;
    LVP_J?: number;
    LVP_H?: number;
  };
  ParamD: {
    LVP_S?: number;
    LVP_J?: number;
    LVP_H?: number;
    LVP_D?: number;
    LVP_DR: number;
  };
};

type Co1PatakaDetailParams = {
  DSTP_AS?: number;
  DSTP_PS?: number;
  DSTP_PSA?: number;
  DSTP_PSS?: number;
  DSTP_DR: number;
};

export type Co1AnalysisResult = AnalysisResult<"co1">;
export type Co1DetailedAnalysisResult = Record<
  string,
  Co1LongVowelDetailParams | Co1PatakaDetailParams
>;

export type ConvertResultType = {
  Co1_result: string;
  Co1_score: string | number;
  ParamA_LVP_S: string | number;
  ParamA_LVP_J: string | number;
  ParamA_LVP_H: string | number;
  ParamB_LVP_S: string | number;
  ParamB_LVP_J: string | number;
  ParamB_LVP_H: string | number;
  ParamC_LVP_S: string | number;
  ParamC_LVP_J: string | number;
  ParamC_LVP_H: string | number;
  ParamD_LVP_S: string | number;
  ParamD_LVP_J: string | number;
  ParamD_LVP_H: string | number;
  ParamD_LVP_D: string | number;
  ParamD_LVP_DR: string | number;
  DSTP_AS: string | number;
  DSTP_PS: string | number;
  DSTP_PSA: string | number;
  DSTP_PSS: string | number;
  DSTP_DR: string | number;
};

const SUB_PARAMS_TITLE_LIST = ["Result.Dementia_score"];

const DEFAULT_DASHBOARD_MAIN_PARAM: ResultMainParam = {
  title: "Result.Dementia_rank",
  // border: "solid 3px #EFB6CB",
  value: undefined,
};

const ERROR_DASHBOARD_DATA: ResultDashboardData = {
  mainParam: DEFAULT_DASHBOARD_MAIN_PARAM,
  subParams: [
    {
      title: SUB_PARAMS_TITLE_LIST[0],
      value: undefined,
    },
  ],
  supplementaryInfo: [],
  diagramIndex: [],
  diagramParams: [],
};

export function ConvertResult({
  result,
}: {
  result: Co1AnalysisResult;
}): ConvertResultType {
  const errorResponse: ConvertResultType = {
    Co1_result: "-",
    Co1_score: "-",
    ParamA_LVP_S: "-",
    ParamA_LVP_J: "-",
    ParamA_LVP_H: "-",
    ParamB_LVP_S: "-",
    ParamB_LVP_J: "-",
    ParamB_LVP_H: "-",
    ParamC_LVP_S: "-",
    ParamC_LVP_J: "-",
    ParamC_LVP_H: "-",
    ParamD_LVP_S: "-",
    ParamD_LVP_J: "-",
    ParamD_LVP_H: "-",
    ParamD_LVP_D: "-",
    ParamD_LVP_DR: "-",
    DSTP_AS: "-",
    DSTP_PS: "-",
    DSTP_PSA: "-",
    DSTP_PSS: "-",
    DSTP_DR: "-",
  };

  if (!result) {
    return errorResponse;
  }

  const detailed = result.detailed_analysis_result as
    Co1DetailedAnalysisResult | undefined;
  if (!detailed) {
    return Object.assign({}, errorResponse, {
      Co1_result: result.analysis_result.co1_result,
      Co1_score: result.analysis_result.co1_score,
    });
  }

  const DEFAULT_VALUE = 0.0;

  // プロパティ名からサブパラメーターの種類を振り分ける
  const { longVowel, pataka } = Object.values(detailed).reduce(
    (acc, item) => {
      if ("ParamD" in item) {
        acc.longVowel = item as Co1LongVowelDetailParams;
      } else if ("DSTP_DR" in item) {
        acc.pataka = item as Co1PatakaDetailParams;
      }
      return acc;
    },
    {} as {
      longVowel?: Co1LongVowelDetailParams;
      pataka?: Co1PatakaDetailParams;
    },
  );

  const fmt = (param: number | undefined, digits: number): string => {
    const v = param ?? DEFAULT_VALUE;
    return isNaN(v) ? "-" : v.toFixed(digits);
  };

  return {
    Co1_result: result.analysis_result.co1_result,
    Co1_score: result.analysis_result.co1_score,
    // 長母音
    ParamA_LVP_S: fmt(longVowel?.ParamA?.LVP_S, 4),
    ParamA_LVP_J: fmt(longVowel?.ParamA?.LVP_J, 4),
    ParamA_LVP_H: fmt(longVowel?.ParamA?.LVP_H, 2),
    ParamB_LVP_S: fmt(longVowel?.ParamB?.LVP_S, 4),
    ParamB_LVP_J: fmt(longVowel?.ParamB?.LVP_J, 4),
    ParamB_LVP_H: fmt(longVowel?.ParamB?.LVP_H, 2),
    ParamC_LVP_S: fmt(longVowel?.ParamC?.LVP_S, 4),
    ParamC_LVP_J: fmt(longVowel?.ParamC?.LVP_J, 4),
    ParamC_LVP_H: fmt(longVowel?.ParamC?.LVP_H, 2),
    ParamD_LVP_S: fmt(longVowel?.ParamD?.LVP_S, 4),
    ParamD_LVP_J: fmt(longVowel?.ParamD?.LVP_J, 4),
    ParamD_LVP_H: fmt(longVowel?.ParamD?.LVP_H, 2),
    ParamD_LVP_D: fmt(longVowel?.ParamD?.LVP_D, 1),
    ParamD_LVP_DR: fmt(longVowel?.ParamD?.LVP_DR, 1),
    // パタカ
    DSTP_AS: fmt(pataka?.DSTP_AS, 1),
    DSTP_PS: fmt(pataka?.DSTP_PS, 1),
    DSTP_PSA: fmt(pataka?.DSTP_PSA, 1),
    DSTP_PSS: fmt(pataka?.DSTP_PSS, 1),
    DSTP_DR: fmt(pataka?.DSTP_DR, 1),
  };
}

// /analysis/co1のレスポンスにおけるCo1スコアのパス
export const AnalysisResultCo1ScorePath = "co1_score";

interface EngineProps {
  _analysisType?: AnalysisEngineType | null;
}

export class Co1Engine extends CoreAnalysisEngine<"co1"> {
  _engineType: EngineType = "co1";
  _analysisType: AnalysisEngineType | null = null;

  constructor(props?: EngineProps) {
    super("co1");
    if (props && props._analysisType) {
      this._analysisType = props._analysisType;
    }
  }

  async getAverage(companyId?: string): Promise<MeanResult | null> {
    const params = {
      engine: "co1",
      data_path: "co1_score",
      company_id: companyId,
    };
    try {
      const response = await appAxios.get("/analysis/statistics/mean", {
        params,
      });
      return response.data;
    } catch {
      return null;
    }
  }

  private static convertToDashboardData(
    result: Co1AnalysisResult,
  ): ResultDashboardData {
    const mainParam: ResultMainParam = {
      ...DEFAULT_DASHBOARD_MAIN_PARAM,
      value: result.analysis_result.co1_result,
    };
    const subParams: ResultSubParam[] = [
      {
        title: SUB_PARAMS_TITLE_LIST[0],
        value: result.analysis_result.co1_score,
      },
    ];

    return {
      mainParam,
      subParams,
      supplementaryInfo: [],
      diagramIndex: [],
      diagramParams: [],
    };
  }

  async renderResult(
    key: number,
    fileId: string,
    t: TFunction,
  ): Promise<ReactElement> {
    const result = await this.getResult(fileId);
    return this._analysisType === "Co1Demo" ? (
      <ResultCo1Demo
        key={key}
        title={t("Result.Dementia_analysis_result")}
        co1_result={result ? result.analysis_result.co1_result : ""}
      />
    ) : (
      <ResultDashboard
        key={key}
        title={t("Result.Dementia_analysis_result") + "(Co-1)"}
        result={
          result
            ? Co1Engine.convertToDashboardData(result)
            : ERROR_DASHBOARD_DATA
        }
      />
    );
  }
}
